import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AGENTS } from '../agents.js';
import AgentMark from '../components/AgentMark.jsx';
import { fetchBrief, fetchPending, approveEntry, rejectEntry, reviseEntry, toRoomCard, EXPERIENCE_MAP } from '../room-model.js';
import { fetchQueue } from '../desk-model.js';

// Brief — the canonical state. Each section shows its approved canon; the Experience
// Map renders as room cards; pending proposals appear inline under their section with
// Approve / Reject / Revise. Approving is spatial: you act where the work lives.
export default function Brief({ projectId, onChanged }) {
  const [brief, setBrief] = useState(null);
  const [pending, setPending] = useState([]);
  const [openAsks, setOpenAsks] = useState([]);
  const [closeChoice, setCloseChoice] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(null);
  const [actionErr, setActionErr] = useState('');
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const reload = useCallback(async () => {
    const [b, p, q] = await Promise.all([
      fetchBrief(projectId).catch(() => null),
      fetchPending(projectId).catch(() => null),
      fetchQueue(projectId).catch(() => null),
    ]);
    if (!alive.current) return;
    if (b) setBrief(b);
    if (p) setPending(p.entries || []);
    if (q) setOpenAsks(q.open || []);
    setLoaded(true);
    if (onChanged) onChanged();
  }, [projectId, onChanged]);

  useEffect(() => { reload(); }, [reload]);

  async function act(fn, entryId) {
    setBusy(entryId); setActionErr('');
    try { await fn(); }
    catch (e) { if (alive.current) setActionErr('That proposal could not be actioned, it may have already been handled. Refreshing.'); }
    finally { await reload(); if (alive.current) setBusy(null); }
  }
  const onApprove = (e) => act(() => approveEntry(projectId, e._id, closeChoice[e._id] || undefined), e._id);
  const onReject = (e) => {
    const note = window.prompt('Why send it back? (optional)') || '';
    return act(() => rejectEntry(projectId, e._id, note), e._id);
  };
  const onRevise = (e) => {
    const prose = window.prompt('Revised text:', e.prose || '');
    if (prose == null) return;
    return act(() => reviseEntry(projectId, e._id, { prose }), e._id);
  };

  if (!loaded || !brief) return null;

  const sections = brief.sections || [];
  const pendingBySection = pending.reduce((m, e) => { (m[e.section] = m[e.section] || []).push(e); return m; }, {});
  const totalApproved = sections.reduce((n, s) => n + (s.entries || []).length, 0);

  return (
    <div className="brief">
      <div className="brief-head">
        <span className="mono-label">THE BRIEF</span>
        <h1>The room, as it stands</h1>
        <div className="brief-sub">
          {totalApproved} approved {totalApproved === 1 ? 'entry' : 'entries'}
          {pending.length > 0 && <> · <span className="em">{pending.length} pending your call</span></>}
        </div>
      </div>
      {actionErr && <div className="action-err">{actionErr}</div>}

      {sections.map(section => {
        const entries = section.entries || [];
        const pend = pendingBySection[section.id] || [];
        if (!entries.length && !pend.length) return null;
        const isMap = section.id === EXPERIENCE_MAP;
        return (
          <section className="brief-section" key={section.id}>
            <div className="bs-head"><h2>{section.title}</h2><span className="bs-count">{entries.length}</span></div>

            {isMap ? (
              <div className="room-grid">
                {entries.map((e, i) => {
                  const card = toRoomCard(e);
                  const a = AGENTS[card.agent];
                  return (
                    <div className="room-card" key={card.id}>
                      <div className="rc-top">
                        <span className="rc-n">{String(i + 1).padStart(2, '0')}</span>
                        <span className="rc-name">{card.name || 'Untitled room'}</span>
                        {card.kind && <span className={`rc-kind ${card.kind}`}>{card.kind}</span>}
                      </div>
                      <p className="rc-body">{card.body}</p>
                      <div className="rc-foot"><AgentMark agent={a} size="xs" /><span className="rc-agent">{(a ? a.name : card.agent).toUpperCase()}</span></div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bs-entries">
                {entries.map(e => {
                  const a = AGENTS[e.agent];
                  return (
                    <div className="canon-entry" key={e._id}>
                      <p>{e.prose}</p>
                      <div className="ce-prov"><AgentMark agent={a} size="xs" /><span>{(a ? a.name : e.agent).toUpperCase()}</span></div>
                    </div>
                  );
                })}
              </div>
            )}

            {pend.map(e => {
              const a = AGENTS[e.agent];
              const acting = busy === e._id;
              return (
                <div className="pending-card" key={e._id}>
                  <div className="pc-tag"><span className="mono-label">PENDING</span><AgentMark agent={a} size="xs" /><span className="pc-agent">{(a ? a.name : e.agent).toUpperCase()} proposes</span></div>
                  <p className="pc-body">{e.prose}</p>
                  {openAsks.length > 0 && (
                    <select
                      className="pc-closes"
                      value={closeChoice[e._id] || ''}
                      onChange={ev => setCloseChoice(prev => ({ ...prev, [e._id]: ev.target.value }))}
                    >
                      <option value="">Does not answer an open ask</option>
                      {openAsks.map(ask => {
                        const label = (ask.agent ? ask.agent.toUpperCase() + ': ' : '') + (ask.ask && ask.ask.text ? ask.ask.text.slice(0, 60) : '');
                        return <option key={ask.askId} value={ask.askId}>{label}</option>;
                      })}
                    </select>
                  )}
                  <div className="pending-actions">
                    <button type="button" className="btn small accent" disabled={acting} onClick={() => onApprove(e)}>Approve</button>
                    <button type="button" className="btn small" disabled={acting} onClick={() => onRevise(e)}>Revise</button>
                    <button type="button" className="btn small ghost" disabled={acting} onClick={() => onReject(e)}>Reject</button>
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}

      {(() => {
        const knownSections = new Set(sections.map(s => s.id));
        const orphans = pending.filter(e => !knownSections.has(e.section));
        if (!orphans.length) return null;
        return orphans.map(e => {
          const a = AGENTS[e.agent];
          const acting = busy === e._id;
          return (
            <div className="pending-card" key={e._id}>
              <div className="pc-tag"><span className="mono-label">PENDING</span><AgentMark agent={a} size="xs" /><span className="pc-agent">{(a ? a.name : e.agent).toUpperCase()} proposes</span></div>
              <p className="pc-body">{e.prose}</p>
              {openAsks.length > 0 && (
                <select
                  className="pc-closes"
                  value={closeChoice[e._id] || ''}
                  onChange={ev => setCloseChoice(prev => ({ ...prev, [e._id]: ev.target.value }))}
                >
                  <option value="">Does not answer an open ask</option>
                  {openAsks.map(ask => {
                    const label = (ask.agent ? ask.agent.toUpperCase() + ': ' : '') + (ask.ask && ask.ask.text ? ask.ask.text.slice(0, 60) : '');
                    return <option key={ask.askId} value={ask.askId}>{label}</option>;
                  })}
                </select>
              )}
              <div className="pending-actions">
                <button type="button" className="btn small accent" disabled={acting} onClick={() => onApprove(e)}>Approve</button>
                <button type="button" className="btn small" disabled={acting} onClick={() => onRevise(e)}>Revise</button>
                <button type="button" className="btn small ghost" disabled={acting} onClick={() => onReject(e)}>Reject</button>
              </div>
            </div>
          );
        });
      })()}

      {totalApproved === 0 && pending.length === 0 && (
        <div className="brief-empty">Nothing approved yet. As the room proposes, entries land here for your call, and the Experience Map fills in room by room.</div>
      )}
    </div>
  );
}
